import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import { propertyKeys } from '../queries';
import { flatFamilyFailureMessage } from './flatFamilyFailure';

export const wholeFlatPreviewKey = (roomId: string | null, wholeFlatId: string | null) =>
  ['whole-flat-preview', roomId, wholeFlatId] as const;

/** Nach jeder Änderung: alle Apartments des Verbunds und das Objekt neu laden. */
export function useInvalidateFlatFamily() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: propertyKeys.details() }),
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: ['property-groups'] }),
    ]);
}

/**
 * Prüfen vor Speichern (§11): sobald Zimmer und Gesamtwohnung feststehen, prüft der Server die
 * Überschneidungen von selbst; gespeichert wird nur, was er geprüft hat. Lehnt er beim Speichern
 * doch ab, zeigt die Vorschau seinen neuen Stand.
 */
export function useWholeFlatLink(
  roomId: string | null,
  wholeFlatId: string | null,
  onLinked: () => void,
) {
  const api = useApi();
  const invalidate = useInvalidateFlatFamily();
  const [failure, setFailure] = useState<string | null>(null);
  const preview = useQuery({
    queryKey: wholeFlatPreviewKey(roomId, wholeFlatId),
    queryFn: () => api.properties.wholeFlat.preview(roomId!, wholeFlatId!),
    enabled: !!roomId && !!wholeFlatId,
    retry: false,
    staleTime: 0,
    // Je Auswahl genau eine Prüfung; neu geprüft wird auf Änderung oder „Erneut prüfen“.
    refetchOnWindowFocus: false,
  });

  const link = useMutation({
    mutationFn: () => api.properties.wholeFlat.link(roomId!, wholeFlatId!),
    // Offline pausiert TanStack sonst still, und der Knopf dreht sich ohne Ende.
    networkMode: 'always',
    onMutate: () => setFailure(null),
    onSuccess: async (result) => {
      if (!result.linked) {
        await preview.refetch();
        return;
      }
      await invalidate();
      toast.success('Wohnungsverbund verknüpft');
      onLinked();
    },
    onError: async (error) => {
      const problem = await toApiError(error);
      setFailure(flatFamilyFailureMessage(problem));
      if (problem.status === 0) await invalidate();
      else await preview.refetch();
    },
  });

  return { preview, link, failure, clearFailure: () => setFailure(null) };
}

/** Lösen eines Zimmers; derselbe Fehlersatz wie beim Verknüpfen. */
export function useWholeFlatUnlink(roomId: string, onUnlinked: () => void) {
  const api = useApi();
  const invalidate = useInvalidateFlatFamily();
  const [failure, setFailure] = useState<string | null>(null);
  const unlink = useMutation({
    mutationFn: () => api.properties.wholeFlat.unlink(roomId),
    networkMode: 'always',
    onMutate: () => setFailure(null),
    onSuccess: async () => {
      await invalidate();
      toast.success('Verknüpfung gelöst');
      onUnlinked();
    },
    onError: async (error) => {
      const problem = await toApiError(error);
      setFailure(flatFamilyFailureMessage(problem));
      if (problem.status === 0) await invalidate();
    },
  });
  return { unlink, failure };
}
